"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.HTTP_STATUS_CODE_VALUES = exports.HTTP_STATUS_CODES = exports.HEALTH_VALUES = exports.HEALTH_STATUS = exports.HEALTHY_HEALTH_VALUE = exports.DNS_RESPONSE_CODES = exports.DNS_CODE_VALUES = void 0;
/**
 * Canonical value maps for enumeration-type units.
 *
 * These live in shared (rather than the React app) because they are the source
 * of truth for the `Values` map attached to enum metrics in the measurement
 * dictionary. Populating `Values` is what makes a metric recognizable as an
 * enum (see `isEnumItem`), which in turn makes it selectable as a dimension and
 * filterable — the same mechanism NMS uses for Oper/Admin status.
 */

/**
 * Health status codes mapping (matches Go backend HealthMapping)
 * These are the numeric values stored in the database
 */
const HEALTH_STATUS = exports.HEALTH_STATUS = {
  100: {
    label: 'Critical',
    color: '#d32f2f'
  },
  200: {
    label: 'Warning',
    color: '#f57c00'
  },
  300: {
    label: 'Healthy',
    color: '#388e3c'
  }
};
/** The health value considered "good" — used for timeline coloring and chart thresholds. */
const HEALTHY_HEALTH_VALUE = exports.HEALTHY_HEALTH_VALUE = 300;

/**
 * DNS response codes (RCODE) per RFC 1035 and extensions.
 * `short` is the canonical IANA mnemonic; `label` is what's shown in enum
 * dropdowns/axis labels (matches `short` except for 0, kept as the friendlier
 * "No Error" since that's the text already surfaced in the UDE filter/chart UI);
 * `description` is the formal RCODE name shown in synthetic-test results/forms.
 * https://www.iana.org/assignments/dns-parameters/dns-parameters.xhtml#dns-parameters-6
 */
const DNS_RESPONSE_CODES = exports.DNS_RESPONSE_CODES = {
  0: {
    short: 'NoError',
    label: 'No Error',
    description: 'No Error'
  },
  1: {
    short: 'FormErr',
    label: 'FormErr',
    description: 'Format Error'
  },
  2: {
    short: 'ServFail',
    label: 'ServFail',
    description: 'Server Failure'
  },
  3: {
    short: 'NXDomain',
    label: 'NXDomain',
    description: 'Non-Existent Domain'
  },
  4: {
    short: 'NotImp',
    label: 'NotImp',
    description: 'Not Implemented'
  },
  5: {
    short: 'Refused',
    label: 'Refused',
    description: 'Query Refused'
  },
  6: {
    short: 'YXDomain',
    label: 'YXDomain',
    description: 'Name Exists when it should not'
  },
  7: {
    short: 'YXRRSet',
    label: 'YXRRSet',
    description: 'RR Set Exists when it should not'
  },
  8: {
    short: 'NXRRSet',
    label: 'NXRRSet',
    description: 'RR Set that should exist does not'
  },
  9: {
    short: 'NotAuth',
    label: 'NotAuth',
    description: 'Not Authorized'
  },
  10: {
    short: 'NotZone',
    label: 'NotZone',
    description: 'Name not contained in zone'
  },
  11: {
    short: 'DSOTYPENI',
    label: 'DSOTYPENI',
    description: 'DSO-TYPE Not Implemented'
  },
  16: {
    short: 'BADVERS',
    label: 'BADVERS',
    description: 'Bad OPT Version'
  },
  17: {
    short: 'BADKEY',
    label: 'BADKEY',
    description: 'Key not recognized'
  },
  18: {
    short: 'BADTIME',
    label: 'BADTIME',
    description: 'Signature out of time window'
  },
  19: {
    short: 'BADMODE',
    label: 'BADMODE',
    description: 'Bad TKEY Mode'
  },
  20: {
    short: 'BADNAME',
    label: 'BADNAME',
    description: 'Duplicate key name'
  },
  21: {
    short: 'BADALG',
    label: 'BADALG',
    description: 'Algorithm not supported'
  },
  22: {
    short: 'BADTRUNC',
    label: 'BADTRUNC',
    description: 'Bad Truncation'
  },
  23: {
    short: 'BADCOOKIE',
    label: 'BADCOOKIE',
    description: 'Bad/missing Server Cookie'
  }
};
/**
 * HTTP status codes per RFC 7231 and extensions
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Status
 */
const HTTP_STATUS_CODES = exports.HTTP_STATUS_CODES = {
  // 1xx Informational
  100: {
    label: 'Continue',
    category: 'Informational'
  },
  101: {
    label: 'Switching Protocols',
    category: 'Informational'
  },
  // 2xx Success
  200: {
    label: 'OK',
    category: 'Success'
  },
  201: {
    label: 'Created',
    category: 'Success'
  },
  202: {
    label: 'Accepted',
    category: 'Success'
  },
  203: {
    label: 'Non-Authoritative Information',
    category: 'Success'
  },
  204: {
    label: 'No Content',
    category: 'Success'
  },
  205: {
    label: 'Reset Content',
    category: 'Success'
  },
  206: {
    label: 'Partial Content',
    category: 'Success'
  },
  // 3xx Redirection
  300: {
    label: 'Multiple Choices',
    category: 'Redirection'
  },
  301: {
    label: 'Moved Permanently',
    category: 'Redirection'
  },
  302: {
    label: 'Found',
    category: 'Redirection'
  },
  303: {
    label: 'See Other',
    category: 'Redirection'
  },
  304: {
    label: 'Not Modified',
    category: 'Redirection'
  },
  305: {
    label: 'Use Proxy',
    category: 'Redirection'
  },
  307: {
    label: 'Temporary Redirect',
    category: 'Redirection'
  },
  308: {
    label: 'Permanent Redirect',
    category: 'Redirection'
  },
  // 4xx Client Errors
  400: {
    label: 'Bad Request',
    category: 'Client Error'
  },
  401: {
    label: 'Unauthorized',
    category: 'Client Error'
  },
  403: {
    label: 'Forbidden',
    category: 'Client Error'
  },
  404: {
    label: 'Not Found',
    category: 'Client Error'
  },
  405: {
    label: 'Method Not Allowed',
    category: 'Client Error'
  },
  406: {
    label: 'Not Acceptable',
    category: 'Client Error'
  },
  407: {
    label: 'Proxy Authentication Required',
    category: 'Client Error'
  },
  408: {
    label: 'Request Timeout',
    category: 'Client Error'
  },
  409: {
    label: 'Conflict',
    category: 'Client Error'
  },
  410: {
    label: 'Gone',
    category: 'Client Error'
  },
  411: {
    label: 'Length Required',
    category: 'Client Error'
  },
  412: {
    label: 'Precondition Failed',
    category: 'Client Error'
  },
  413: {
    label: 'Request Entity Too Large',
    category: 'Client Error'
  },
  414: {
    label: 'Request-URI Too Long',
    category: 'Client Error'
  },
  415: {
    label: 'Unsupported Media Type',
    category: 'Client Error'
  },
  416: {
    label: 'Requested Range Not Satisfiable',
    category: 'Client Error'
  },
  417: {
    label: 'Expectation Failed',
    category: 'Client Error'
  },
  422: {
    label: 'Unprocessable Entity',
    category: 'Client Error'
  },
  429: {
    label: 'Too Many Requests',
    category: 'Client Error'
  },
  // 5xx Server Errors
  500: {
    label: 'Internal Server Error',
    category: 'Server Error'
  },
  501: {
    label: 'Not Implemented',
    category: 'Server Error'
  },
  502: {
    label: 'Bad Gateway',
    category: 'Server Error'
  },
  503: {
    label: 'Service Unavailable',
    category: 'Server Error'
  },
  504: {
    label: 'Gateway Timeout',
    category: 'Server Error'
  },
  505: {
    label: 'HTTP Version Not Supported',
    category: 'Server Error'
  }
};
/**
 * Convert a code → metadata map into the `Record<number, string>` shape used by
 * `MeasurementMetric.Values` / `MeasurementDimension.Values`.
 */
function toValuesMap(codes, {
  withCode = true
} = {}) {
  return Object.entries(codes).reduce((acc, [code, info]) => {
    acc[Number(code)] = withCode ? `${info.label} (${code})` : info.label;
    return acc;
  }, {});
}

/** `Values` map for metrics with `Unit: 'health'`. */
const HEALTH_VALUES = exports.HEALTH_VALUES = toValuesMap(HEALTH_STATUS, {
  withCode: false
});

/** `Values` map for metrics with `Unit: 'dns_code'`. */
const DNS_CODE_VALUES = exports.DNS_CODE_VALUES = toValuesMap(DNS_RESPONSE_CODES, {
  withCode: false
});

/** `Values` map for metrics with `Unit: 'http_status_code'`. */
const HTTP_STATUS_CODE_VALUES = exports.HTTP_STATUS_CODE_VALUES = toValuesMap(HTTP_STATUS_CODES, {
  withCode: false
});